"use client";

import React, { createContext, useCallback, useContext, useEffect, useState } from "react";

import { v4 as uuidv4 } from "uuid";

import {
  type SavedSearch,
  type StoredSearch,
  createSavedSearch as createSavedSearchAPI,
  deleteSavedSearch as deleteSavedSearchAPI,
  listSavedSearchesForUser,
  updateSavedSearch as updateSavedSearchAPI,
} from "@/lib/shop-agent-client";

import { useAuth } from "./AuthContext";

interface CreateSavedSearchInput {
  name: string;
  searchQuery: StoredSearch;
  sourceSearchId?: string | null;
  threadId?: string | null;
}

interface SavedSearchesContextType {
  savedSearches: SavedSearch[];
  loading: boolean;
  error: string | null;
  createSavedSearch: (input: CreateSavedSearchInput) => Promise<SavedSearch | null>;
  deleteSavedSearch: (id: string) => Promise<boolean>;
  updateSavedSearch: (id: string, updates: Partial<SavedSearch>) => Promise<boolean>;
  fetchSavedSearches: () => Promise<void>;
}

const SavedSearchesContext = createContext<SavedSearchesContextType | undefined>(undefined);

export function SavedSearchesProvider({ children }: { children: React.ReactNode }) {
  const [savedSearches, setSavedSearches] = useState<SavedSearch[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { user, isAuthenticated } = useAuth();

  const fetchSavedSearches = useCallback(async () => {
    if (!user || !isAuthenticated) {
      setSavedSearches([]);
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);
    try {
      setSavedSearches(await listSavedSearchesForUser());
    } catch (err) {
      console.error("Failed to list saved searches:", err);
      setError("Failed to load saved searches");
      setSavedSearches([]);
    } finally {
      setLoading(false);
    }
  }, [user, isAuthenticated]);

  useEffect(() => {
    fetchSavedSearches();
  }, [fetchSavedSearches]);

  const createSavedSearch = async (input: CreateSavedSearchInput): Promise<SavedSearch | null> => {
    if (!user) return null;

    try {
      const savedSearch = await createSavedSearchAPI({
        id: uuidv4(),
        name: input.name,
        search_query: input.searchQuery,
        source_search_id: input.sourceSearchId ?? undefined,
        thread_id: input.threadId ?? undefined,
        is_public: false,
      });
      await fetchSavedSearches();
      return savedSearch;
    } catch (err) {
      console.error("Failed to create saved search:", err);
      return null;
    }
  };

  const updateSavedSearch = async (id: string, updates: Partial<SavedSearch>): Promise<boolean> => {
    if (!user) return false;

    try {
      await updateSavedSearchAPI(id, {
        name: updates.name,
        search_query: updates.search_query,
        source_search_id: updates.source_search_id,
        thread_id: updates.thread_id,
        is_public: updates.is_public,
      });
      await fetchSavedSearches();
      return true;
    } catch (err) {
      console.error("Failed to update saved search:", err);
      return false;
    }
  };

  const deleteSavedSearch = async (id: string): Promise<boolean> => {
    if (!user) return false;

    try {
      await deleteSavedSearchAPI(id);
      await fetchSavedSearches();
      return true;
    } catch (err) {
      console.error("Failed to delete saved search:", err);
      return false;
    }
  };

  return (
    <SavedSearchesContext.Provider
      value={{
        savedSearches,
        loading,
        error,
        createSavedSearch,
        deleteSavedSearch,
        updateSavedSearch,
        fetchSavedSearches,
      }}
    >
      {children}
    </SavedSearchesContext.Provider>
  );
}

export const useSavedSearches = () => {
  const context = useContext(SavedSearchesContext);
  if (!context) {
    throw new Error("useSavedSearches must be used within a SavedSearchesProvider");
  }
  return context;
};
